/**
 * Phase 15.5.2 — Top-level ErrorBoundary
 * --------------------------------------
 * Catches any uncaught React render error so a single broken component
 * (e.g. an undefined helper imported from another file) can never blank
 * the whole app like it did with the missing `formatDisplayPlays` import
 * in FollowingFeed.jsx (post-Phase-15.2 regression).
 *
 * Behaviour:
 *   - Renders a friendly, branded fallback screen with the error message
 *     visible to users so they can screenshot it for support.
 *   - "Try again" button resets the boundary so a recovered child can
 *     re-mount without a full page reload (often the parent state has
 *     already moved past the bad data).
 *   - "Reload page" forces a hard reload.
 *   - In development (NODE_ENV !== "production"), also dumps the stack
 *     trace so the agent / dev can pin it instantly.
 *   - Pushes the error to the backend diagnostics ring buffer via
 *     /api/admin/status/diagnostics's sibling endpoint when authed, so
 *     the founder's runtime status page shows client-side crashes too.
 *     Failures here are swallowed — never let the reporter crash the
 *     fallback UI.
 */
import { Component } from "react";

const reportToBackend = async (error, info) => {
  try {
    const backend = process.env.REACT_APP_BACKEND_URL;
    if (!backend) return;
    await fetch(`${backend}/api/client-errors`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // No auth header required — endpoint is a public crash sink with
      // size limits, same as our share proxy.
      body: JSON.stringify({
        path: typeof window !== "undefined" ? window.location.pathname : null,
        message: String(error?.message || error)?.slice(0, 500),
        stack: String(error?.stack || "")?.slice(0, 4000),
        component_stack: String(info?.componentStack || "")?.slice(0, 4000),
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      }),
      keepalive: true,
    });
  } catch { /* never let the reporter crash the fallback UI */ }
};

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // Log to the browser console — keeps existing devtools workflow.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] uncaught render error:", error, info);
    reportToBackend(error, info);
  }

  reset = () => {
    this.setState({ hasError: false, error: null, info: null });
  };

  hardReload = () => {
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const isDev = process.env.NODE_ENV !== "production";
    const message = String(this.state.error?.message || this.state.error || "Unknown error");

    return (
      <div
        className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-[#1a0729] via-[#0f0518] to-black text-white"
        data-testid="error-boundary-fallback"
      >
        <div className="max-w-md w-full text-center">
          <div className="text-6xl mb-4" aria-hidden="true">😵‍💫</div>
          <h1 className="text-2xl font-black mb-2" data-testid="error-boundary-title">
            Something broke on this screen
          </h1>
          <p className="text-pink-200/70 text-sm mb-6">
            Don't worry — your account and music are safe. We logged the issue
            so the team can fix it. You can usually keep going by retrying or
            reloading.
          </p>

          <div className="flex gap-2 justify-center mb-6">
            <button
              onClick={this.reset}
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-pink-600 to-purple-600 text-white font-bold hover:opacity-90"
              data-testid="error-boundary-retry"
            >
              Try again
            </button>
            <button
              onClick={this.hardReload}
              className="px-4 py-2 rounded-lg bg-white/10 border border-white/15 text-white font-bold hover:bg-white/15"
              data-testid="error-boundary-reload"
            >
              Reload page
            </button>
          </div>

          {/* Show a one-line cause to power users + screenshots. */}
          <div
            className="text-[11px] text-pink-200/40 font-mono break-words"
            data-testid="error-boundary-message"
          >
            {message}
          </div>

          {isDev && this.state.info?.componentStack && (
            <pre
              className="mt-3 text-[10px] text-pink-200/40 font-mono whitespace-pre-wrap text-left bg-black/30 p-3 rounded max-h-[200px] overflow-auto"
              data-testid="error-boundary-stack"
            >
              {this.state.info.componentStack}
            </pre>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
